import './PlanCycleSummary.css';

/** Meses de cada ciclo de cobrança; espelha o enum Periodicity do backend. */
const PERIODICITY_MONTHS = {
    MONTHLY: 1,
    BIMONTHLY: 2,
    QUARTERLY: 3,
    SEMI_ANNUAL: 6,
    YEARLY: 12,
    BIENNIAL: 24
};

const plural = (count, singular, pluralForm) => (count === 1 ? singular : pluralForm);

/** "todo mês", "a cada 3 meses", "todo ano": como o cliente enxerga a frequência da cobrança. */
const describeFrequency = (cycleMonths) => {
    if (cycleMonths === 1) return 'todo mês';
    if (cycleMonths === 12) return 'todo ano';
    if (cycleMonths % 12 === 0) return `a cada ${cycleMonths / 12} anos`;
    return `a cada ${cycleMonths} meses`;
};

/** "12 meses (1 ano)": o tempo total, com o equivalente em anos quando é número redondo. */
const describeTotalDuration = (totalMonths) => {
    const months = `${totalMonths} ${plural(totalMonths, 'mês', 'meses')}`;
    if (totalMonths >= 12 && totalMonths % 12 === 0) {
        const years = totalMonths / 12;
        return `${months} (${years} ${plural(years, 'ano', 'anos')})`;
    }
    return months;
};

/**
 * Resumo em linguagem simples de como o plano cobra: frequência e duração.
 *
 * Existe porque periodicidade e quantidade de ciclos são dois campos que, separados, não dizem o
 * que o plano faz. "Mensal" e "12" só viram "cobra todo mês, por 12 meses (1 ano)" depois de uma
 * conta que a recepção não deveria precisar fazer de cabeça.
 *
 * @param {object|null} periodicity opção selecionada ({ value }) ou nula
 * @param {number|string} qtyTransactions número de cobranças; zero ou vazio é "indeterminado"
 * @param {number|string|null} packageDays duração do pacote em dias (só na loja); quando existe,
 *        substitui a frase de periodicidade e ciclos
 */
const PlanCycleSummary = ({ periodicity, qtyTransactions, packageDays = null }) => {
    // Pacote em dias: pago na loja, vale pelo número de dias e encerra sozinho. Não tem ciclos de
    // cobrança nem provedor, então a frase é outra.
    const days = Number(packageDays) || 0;
    if (days > 0) {
        return (
            <div className="plan-cycle-summary" role="status">
                <span className="plan-cycle-summary__icon" aria-hidden="true">
                    <i className="fa-solid fa-store"></i>
                </span>
                <div className="plan-cycle-summary__body">
                    <span className="plan-cycle-summary__eyebrow">Como este plano cobra</span>
                    <p className="plan-cycle-summary__text">
                        Pacote válido por <strong>{days} {plural(days, 'dia', 'dias')}</strong>, pago na loja
                    </p>
                    <span className="plan-cycle-summary__meta">
                        Encerra sozinho no fim. A renovação é um novo pagamento.
                    </span>
                </div>
            </div>
        );
    }

    const cycleMonths = PERIODICITY_MONTHS[periodicity?.value];
    if (!cycleMonths) {
        return null;
    }

    const cycles = Number(qtyTransactions) || 0;
    const indeterminate = cycles === 0;
    const frequency = describeFrequency(cycleMonths);

    return (
        <div className="plan-cycle-summary" role="status">
            <span className="plan-cycle-summary__icon" aria-hidden="true">
                <i className="fa-solid fa-rotate"></i>
            </span>
            <div className="plan-cycle-summary__body">
                <span className="plan-cycle-summary__eyebrow">Como este plano cobra</span>
                <p className="plan-cycle-summary__text">
                    Cobra <strong>{frequency}</strong>,{' '}
                    {indeterminate
                        ? <>até o cliente cancelar</>
                        : <>por <strong>{describeTotalDuration(cycles * cycleMonths)}</strong></>}
                </p>
                {!indeterminate && (
                    <span className="plan-cycle-summary__meta">
                        {cycles} {plural(cycles, 'cobrança', 'cobranças')} no total
                    </span>
                )}
            </div>
        </div>
    );
};

export default PlanCycleSummary;
